import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import { AuthProvider } from './context/AuthContext';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { ModernLayout } from './components/layout/ModernLayout';
import { Login } from './pages/Login';
import { ModernDashboard } from './pages/ModernDashboard';
import { Agents } from './pages/Agents';
import AgentConfig from './pages/AgentConfig';
import { Chatbots } from './pages/Chatbots';
import { CreateChatbot } from './pages/CreateChatbot';
import { Analytics } from './pages/Analytics';
import ChatbotConfig from './pages/ChatbotConfig';
import { Workflows } from './pages/Workflows';
import { Reports } from './pages/Reports';
import { Users } from './pages/Users';
import { Settings } from './pages/Settings';
import { NotFound } from './pages/NotFound';
import { AgentTypes } from './pages/AgentTypes';
import './index.css';
import { ToastContainer } from 'react-toastify';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import 'react-image-crop/dist/ReactCrop.css';
import TenantListing from './pages/SuperAdmin/TenantListing';
import AgentType from './pages/SuperAdmin/AgentType';
import TenantConfig from './pages/SuperAdmin/TenantConfig';
import { ReRouting } from './pages/ReRouting';
import Departments from './pages/Departments';
import UsersList from './pages/UsersList';
import PublicChatWidget from './pages/PublicChatWidget';
import PublicChatWidgetReports from './pages/PublicChatWidgetReports';


/**
 * Main App component that sets up routing and global providers
 * 
 * @returns {JSX.Element} The main application component
 */
const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ToastContainer position="top-right" />
      <Router>
        <AuthProvider>
          <AppProvider>
            <div className="min-h-screen bg-agentic-white-off">
              <Routes>
                {/* Public Routes */}
                <Route 
                  path="/login" 
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <Login />
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="/signup" 
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <Signup />
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/forgot-password" 
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <ForgotPassword />
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="/reset-password/verify" 
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <ResetPassword />
                    </ProtectedRoute>
                  } 
                />

                {/* Public Chat Widget Route - No Auth Required */}
                <Route 
                  path="/chat/:chatbotId/:clientId" 
                  element={<PublicChatWidget />} 
                />

                {/* Public Chat Widget Reports Route - No Auth Required */}
                <Route 
                  path="/chat-reports/:chatbotId/:clientId" 
                  element={<PublicChatWidgetReports />} 
                />

                {/* Protected Routes */}
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <ModernLayout>
                        <Routes>
                          <Route path="/" element={<ReRouting />} />
                          <Route path="/dashboard" element={<ModernDashboard />} />
                          <Route path="/agents" element={<Agents />} />
                          <Route path="/agents/:agentId/config" element={<AgentConfig />} />
                          <Route path="/agent-types" element={<AgentTypes />} />
                          <Route path="/chatbots" element={<Chatbots />} />
                          <Route path="/chatbots/create" element={<CreateChatbot />} />
                          <Route path="/chatbots/:chatbotId/config" element={<ChatbotConfig />} />
                          <Route path="/analytics" element={<Analytics />} />
                          <Route path="/workflows" element={<Workflows />} />
                          <Route path="/reports" element={<Reports />} />
                          {/* <Route path="/users" element={<Users />} /> */}
                          <Route path="/settings" element={<Settings />} />
                          <Route path="/tenant-listing" element={<TenantListing />} />
                          <Route path="/super-agent-type" element={<AgentType />} />
                          <Route path="/tenant/:tenantId/config" element={<TenantConfig />} />
                          <Route path="/departments" element={<Departments />} />
                          <Route path="/users" element={<UsersList />} />
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </ModernLayout>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </div>
          </AppProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
};

export default App;
